import { Box, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Info } from '@scoir/tooltips'

export type InsightsMetricUnit = 'total' | 'percent'

export default function InsightsMetric({
  title,
  tooltipText,
  primaryValue,
  secondaryValue,
}: {
  title: string
  tooltipText: string
  primaryValue: React.ReactNode
  secondaryValue: React.ReactNode
}) {
  return (
    <Paper
      component={Box}
      variant="outlined"
      // Allow Box props
      // @ts-expect-error typing isn't picking up the Box props
      p={2}
    >
      <Box
        display="flex"
        alignItems="center"
      >
        <Typography
          variant="body2"
          color="textSecondary"
        >
          {title}
        </Typography>
        <Box mx={1}>
          <Info
            outlinedIcon
            inheritColor
            tooltipProps={{ placement: 'right' }}
            aria-label={tooltipText}
          >
            {tooltipText}
          </Info>
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Box marginRight={2}>{primaryValue}</Box>
        <Box
          display="flex"
          alignItems="center"
          textAlign="right"
        >
          {secondaryValue}
        </Box>
      </Box>
    </Paper>
  )
}

export function formatPercentage(percentage: number): string {
  const formatterOptions: Intl.NumberFormatOptions = {
    maximumFractionDigits: 1,
    style: 'percent',
  }

  // If the percentage is 0 || >= 100% || <= -100%, don't show any decimal places
  if (percentage === 0 || percentage >= 1 || percentage <= -1) {
    formatterOptions.maximumFractionDigits = 0
  }

  const formatter = new Intl.NumberFormat('en-US', formatterOptions)
  return formatter.format(percentage)
}

export function formatTotal(total: number): string {
  // Default number formatter to insert commas in numbers beyond the thousands
  return new Intl.NumberFormat().format(total)
}

const useSecondaryValueStyles = makeStyles((theme) => ({
  value: {
    color: theme.palette.grey[800],
    fontWeight: 700,
  },
}))

const useSecondaryDeltaPercentageStyles = makeStyles((theme) => ({
  textColorPositive: {
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  textColorNegative: {
    color: theme.palette.error.main,
    fontWeight: 700,
  },
  textColorZero: {
    color: theme.palette.grey[800],
    fontWeight: 700,
  },
}))

export function NullDelta({ label, isPercentage }: { label: string; isPercentage?: boolean }) {
  const styles = useSecondaryValueStyles()
  return (
    <>
      <Typography
        component="span"
        className={styles.value}
      >
        -%
      </Typography>
      <Typography
        component="span"
        variant="body1"
        color="textSecondary"
      >
        &nbsp;{isPercentage ? `vs ${label}` : label}
      </Typography>
    </>
  )
}

export function SecondaryDeltaPercentage({ label, percentage }: { label: string; percentage: number }) {
  const styles = useSecondaryDeltaPercentageStyles()

  if (percentage >= 0) {
    return (
      <>
        <Typography
          component="span"
          className={percentage === 0 ? styles.textColorZero : styles.textColorPositive}
        >
          +{formatPercentage(percentage)}
        </Typography>
        <Typography
          component="span"
          variant="body1"
          color="textSecondary"
        >
          &nbsp;vs {label}
        </Typography>
      </>
    )
  }

  return (
    <>
      <Typography
        component="span"
        className={styles.textColorNegative}
      >
        {formatPercentage(percentage)}
      </Typography>
      <Typography
        component="span"
        variant="body1"
        color="textSecondary"
      >
        &nbsp;vs {label}
      </Typography>
    </>
  )
}

export function SecondaryValue({ label, value }: { label: string; value: React.ReactNode }) {
  const styles = useSecondaryValueStyles()
  return (
    <div>
      <Typography
        component="span"
        className={styles.value}
      >
        {value}
      </Typography>
      <Typography
        component="span"
        variant="body1"
        color="textSecondary"
      >
        &nbsp;{label}
      </Typography>
    </div>
  )
}

const usePrimaryCountDeltaStyles = makeStyles((theme) => ({
  textColorPositive: {
    color: theme.palette.success.main,
  },
  textColorNegative: {
    color: theme.palette.error.main,
  },
  textColorZero: {
    color: theme.palette.grey[800],
  },
}))

export function PrimaryCountDelta({ value }: { value: number }) {
  const styles = usePrimaryCountDeltaStyles()

  if (value >= 0) {
    return (
      <Typography
        component="span"
        variant="h5"
        className={value === 0 ? styles.textColorZero : styles.textColorPositive}
      >
        +{formatTotal(value)}
      </Typography>
    )
  }

  return (
    <>
      <Typography
        component="span"
        variant="h5"
        className={styles.textColorNegative}
      >
        {formatTotal(value)}
      </Typography>
    </>
  )
}
